import { getTheme, mergeStyleSets } from '@fluentui/react';
import HrefLink from 'components/Links/HrefLink';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface CopilotMarkdownProps {
  content?: string;
  withTableStyles?: boolean;
  skipHtml?: boolean;
}

export const CopilotMarkdown = (props: CopilotMarkdownProps) => {
  const theme = getTheme();

  if (!props.content) return null;

  const isInternalLink = (href?: string) => !!href && href.startsWith('/') && !href.startsWith('//');

  const styles = mergeStyleSets({
    content: {
      maxWidth: '100%',
      minWidth: 0,
      overflowX: 'auto',
      wordBreak: 'break-word',
    },
  });

  const baseComponents = {
    a: (anchorProps: React.ComponentProps<'a'>) => {
      // the sanitizer empties an unsafe href (sandbox:, javascript:): the text stays, the link goes
      if (!anchorProps.href) return <span>{anchorProps.children}</span>;

      if (isInternalLink(anchorProps.href)) {
        return (
          <HrefLink href={anchorProps.href} style={anchorProps.style} title={anchorProps.title}>
            {anchorProps.children}
          </HrefLink>
        );
      }

      return (
        <a
          {...anchorProps}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: theme.palette.themePrimary,
            ...(anchorProps.style || {}),
          }}
        />
      );
    },
  };

  const tableComponents = {
    div: (divProps: React.ComponentProps<'div'>) => (
      <div
        {...divProps}
        style={{
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
          ...(divProps.style || {}),
        }}
      />
    ),
    table: (tableProps: React.ComponentProps<'table'>) => (
      <table
        {...tableProps}
        style={{
          borderCollapse: 'collapse',
          minWidth: 420,
          width: '100%',
          ...(tableProps.style || {}),
        }}
      />
    ),
    th: (thProps: React.ComponentProps<'th'>) => (
      <th
        {...thProps}
        style={{
          border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
          padding: '6px 8px',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          ...(thProps.style || {}),
        }}
      />
    ),
    td: (tdProps: React.ComponentProps<'td'>) => (
      <td
        {...tdProps}
        style={{
          border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
          padding: '6px 8px',
          ...(tdProps.style || {}),
        }}
      />
    ),
  };

  const components = props.withTableStyles ? { ...baseComponents, ...tableComponents } : baseComponents;

  return (
    <div className={styles.content}>
      <Markdown remarkPlugins={[remarkGfm]} components={components} skipHtml={props.skipHtml}>
        {props.content}
      </Markdown>
    </div>
  );
};
